// AdminPermissionService.js
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../Services/Firebase/FirebaseIndex";

/**
 * Check if an admin user has a specific permission
 * @param {string} userId - The admin user's Firebase Auth UID
 * @param {string} requiredPermission - The permission to check for
 * @returns {Promise<boolean>} Whether the admin has the permission
 */
export const checkAdminPermission = async (userId, requiredPermission) => {
  try {
    // Get admin user
    const adminRef = doc(db, "AdminUsers", userId);
    const adminDoc = await getDoc(adminRef);
    
    if (!adminDoc.exists() || adminDoc.data().status !== "active") {
      return false;
    }
    
    // Super admins have all permissions
    if (adminDoc.data().isSuperAdmin) {
      return true;
    }
    
    // Check direct permissions
    if (adminDoc.data().permissions?.includes(requiredPermission)) {
      return true;
    }
    
    // Check role-based permissions
    const roleId = adminDoc.data().roleId;
    if (roleId) {
      const roleRef = doc(db, "AdminRoles", roleId);
      const roleDoc = await getDoc(roleRef);
      
      if (roleDoc.exists() && roleDoc.data().permissions?.includes(requiredPermission)) {
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error("Error checking admin permission:", error);
    return false;
  }
};

/**
 * Check if an admin user has a specific role
 * @param {string} userId - The admin user's Firebase Auth UID
 * @param {string} roleName - The role name to check for
 * @returns {Promise<boolean>} Whether the admin has the role
 */
export const hasAdminRole = async (userId, roleName) => {
  try {
    const adminRef = doc(db, "AdminUsers", userId);
    const adminDoc = await getDoc(adminRef);
    
    if (!adminDoc.exists() || adminDoc.data().status !== "active") {
      return false;
    }
    
    // Super admins can access everything
    if (adminDoc.data().isSuperAdmin) {
      return true;
    }
    
    const roleId = adminDoc.data().roleId;
    if (roleId) {
      const roleRef = doc(db, "AdminRoles", roleId);
      const roleDoc = await getDoc(roleRef);
      
      return roleDoc.exists() && roleDoc.data().name === roleName;
    }
    
    return false;
  } catch (error) {
    console.error("Error checking admin role:", error);
    return false;
  }
};

/**
 * Get all permissions available for a specific admin user
 * @param {string} userId - The admin user's Firebase Auth UID
 * @returns {Promise<string[]>} Array of permission strings
 */
export const getAdminPermissions = async (userId) => {
  try {
    const adminRef = doc(db, "AdminUsers", userId);
    const adminDoc = await getDoc(adminRef);
    
    if (!adminDoc.exists() || adminDoc.data().status !== "active") {
      return [];
    }
    
    const permissions = new Set(adminDoc.data().permissions || []);
    
    // If super admin, get all defined permissions
    if (adminDoc.data().isSuperAdmin) {
      const allPermissionsRef = collection(db, "AdminPermissions");
      const permissionsSnapshot = await getDocs(allPermissionsRef);
      
      permissionsSnapshot.forEach((doc) => {
        permissions.add(doc.id);
      });
      
      return Array.from(permissions);
    }
    
    // Add role-based permissions
    const roleId = adminDoc.data().roleId;
    if (roleId) {
      const roleRef = doc(db, "AdminRoles", roleId);
      const roleDoc = await getDoc(roleRef);
      
      if (roleDoc.exists() && roleDoc.data().permissions) {
        roleDoc.data().permissions.forEach(permission => {
          permissions.add(permission);
        });
      }
    }
    
    return Array.from(permissions);
  } catch (error) {
    console.error("Error getting admin permissions:", error);
    return [];
  }
};

/**
 * Log admin action for audit purposes
 * @param {string} adminId - The admin user's Firebase Auth UID
 * @param {string} action - The action performed
 * @param {Object} details - Additional details about the action
 * @returns {Promise<boolean>} Success status
 */
export const logAdminAction = async (adminId, action, details = {}) => {
  try {
    const { addDoc, collection, serverTimestamp } = await import("firebase/firestore");
    
    // Create audit log entry
    await addDoc(collection(db, "AdminAuditLogs"), {
      adminId,
      action,
      details,
      timestamp: serverTimestamp(),
      ipAddress: "N/A" // Would need server-side implementation for real IP
    });
    
    return true;
  } catch (error) {
    console.error("Error logging admin action:", error);
    return false;
  }
};

export default {
  checkAdminPermission,
  hasAdminRole,
  getAdminPermissions,
  logAdminAction
};